import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DeleteIcon, EyeIcon, PenIcon, UserIcon, CopyIcon } from 'src/assets/icons'
import DeleteImg from 'src/assets/images/delete-img.png'
import { Button, Layout, MenuProps, Typography, notification } from 'antd'
import { useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { colorBgButtonSubtleHovered, colorBgSuccessHovered, colorText, error } from 'src/app/themes/primaryThemeDefault'
import DeleteButton from 'src/app/shared/components/common/button/delete-button'
import ActionConfirmationModal from 'src/app/shared/components/modal/action-confirmation-modal'
import { useFileMappingTab } from './useFileMappingTab'
import CommonTag from 'src/app/shared/components/common/tag'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { deleteFileMappingFormats, getListFileMappingFormats } from 'src/app/shared/services/ClientSetupService'

/* Localization TimeZone */
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import ViewClientsModal from '../../view-clients/ViewClientsModal'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { FORMAT_TYPE_VALUE, TEXT_QUALIFIER_VALUE, USER_ROLE } from 'src/app/shared/constants/app.constants'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'
import { useFileMappingSetupFilters } from '../../FileMappingSetupFiltersContext'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'

dayjs.extend(utc)
dayjs.extend(timezone)
/* Localization TimeZone */

const FileMappingTab: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { fileMappingList, getFileMappingList, isDeleting, setIsDeleting, isGettingList } = useFileMappingTab()
  const [isModalConfirmDeleteOpened, setIsModalConfirmDeleteOpened] = useState<boolean>(false)
  const [selectedFileMapping, setSelectedFileMapping] = useState<any>({})
  const [isFileMappingClientsModalOpen, setIsFileMappingClientsModalOpen] = useState(false)
  const [fileMappingId, setFileMappingId] = useState('')
  const [fileMappingName, setFileMappingName] = useState('')
  const [api, contextHolder] = notification.useNotification()
  const { userPermissions, role } = useRolePermission()
  const { saveSearchParams, restoreSearchParams } = useFileMappingSetupFilters()

  const [defaultParams] = useState(() => {
    const restoredParams = restoreSearchParams()
    return restoredParams || null
  })

  const isAutoloadFilemapping = (formatName: string) => {
    const autoloadFileMapping = "Auto load file mapping";
    return formatName.toLowerCase().includes(autoloadFileMapping.toLowerCase());
  }

  const getActionItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        // Edit is gated by per-row scope (isEditable): "View all" widens visibility, not edit rights.
        userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS) && rowData?.isEditable
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('FileMappingSetup:actions.edit_file_mapping')}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.fileMappingSetup}/edit/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('FileMappingSetup:actions.copy_file_mapping')}
                </Typography.Text>
              ),
              icon: <CopyIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.copyFileMapping}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '3',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t('FileMappingSetup:actions.view_file_mapping')}
                </Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.fileMappingSetup}/view/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.VIEW_ITEMS) && !isAutoloadFilemapping(rowData?.formatName)
          ? {
              key: '4',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: colorText }}>
                  {t('FileMappingSetup:actions.view_clients')}
                </Typography.Text>
              ),
              icon: <UserIcon />,
              onClick: () => {
                if (rowData?.id) {
                  setFileMappingName(rowData?.formatName)
                  showFileMappingClients(rowData?.id)
                }
              },
            }
          : null,
        // Delete is gated by per-row scope (isEditable): "View all" widens visibility, not delete rights.
        userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.DELETE_ITEMS) && rowData?.isEditable && !isAutoloadFilemapping(rowData?.formatName)
          ? {
              key: '5',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: error }}>
                  {t('FileMappingSetup:actions.delete_format')}
                </Typography.Text>
              ),
              icon: <DeleteIcon />,
              onClick: () => {
                if (rowData?.id) {
                  setSelectedFileMapping(rowData)
                  setIsModalConfirmDeleteOpened(true)
                }
              },
            }
          : null,
      ]
    },
    [userPermissions, t, navigate],
  )

  const generateCustomColumnDefs = useCallback(
    (colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
      const defs = colDefs.map((item, index) => {
        const customTags = ['fileContainsIssuedDate', 'fileContainsHeaderRow']
        if (customTags.includes(item.colId)) {
          item.cellRenderer = (row: any) => {
            return (
              <>
                {row?.data?.[item.colId]?.toLowerCase() === 'yes' ? (
                  <CommonTag color='success' style={{ background: colorBgSuccessHovered }} displayLeftDot>
                    {t('common:yes')}
                  </CommonTag>
                ) : (
                  <CommonTag color='default' style={{ background: colorBgButtonSubtleHovered }} displayLeftDot>
                    {t('common:no')}
                  </CommonTag>
                )}
              </>
            )
          }
        }

        if (item.colId === 'status') {
          item.cellRenderer = (row: any) => {
            return (
              <>
                {row?.data['status']?.toLowerCase() === 'active' ? (
                  <CommonTag
                    color='success'
                    style={{ background: colorBgSuccessHovered, padding: '4px 12px', width: 'auto' }}
                    displayLeftDot
                  >
                    {t('common:active')}
                  </CommonTag>
                ) : (
                  <CommonTag
                    color='error'
                    style={{ background: colorBgButtonSubtleHovered, padding: '4px 12px', width: 'auto' }}
                    displayLeftDot
                  >
                    {t('common:inactive')}
                  </CommonTag>
                )}
              </>
            )
          }
        }

        if (['dateCreated', 'dateUpdated'].includes(item.colId)) {
          item.cellRenderer = (row: any) => {
            return row?.data[item.colId] ?? ''
          }
        }

        if (item.colId === 'formatType') {
          item.cellRenderer = (row: any) => {
            return (
              <>
                {['csv', 'excel'].includes(row?.data['formatType'])
                  ? FORMAT_TYPE_VALUE[row?.data['formatType']]
                  : row?.data['formatType']}
              </>
            )
          }
        }
        if (item.colId === 'textQualifier') {
          item.cellRenderer = (row: any) => {
            return (
              <>
                {['dash', 'doublequote'].includes(row?.data['textQualifier'])
                  ? TEXT_QUALIFIER_VALUE[row?.data['textQualifier']]
                  : row?.data['textQualifier']}
              </>
            )
          }
        }

        return item
      })
      return defs
    },
    [t],
  )

  const handleDeleteFileMapping = async () => {
    if (selectedFileMapping?.id) {
      setIsDeleting(true)
      deleteFileMappingFormats(selectedFileMapping.id)
        .then(
          (res: any) => {
            setIsDeleting(false)
            api.success({
              message: (
                <RichTextField
                  richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FILE_MAPPING_DELETE_SUCCESSFULLY, {
                    formatName: selectedFileMapping?.formatName,
                  })}
                />
              ),
            })
            handleSearch({})
            setIsDeleting(false)
          },
          (err) => {
            api.error({
              message: err?.message ?? t('common:error'),
            })
          },
        )
        .finally(() => {
          setIsDeleting(false)
          setIsModalConfirmDeleteOpened(false)
        })
    }
  }

  const handleSearch = useCallback(
    async (value: Partial<IGetExportData>) => {
      const paramsToSave = {
        ...value,
        isSearchAllColumns: (value.searchColumns?.length || 0) > 1,
      }
      saveSearchParams(paramsToSave)
      await getFileMappingList(value)
    },
    [getFileMappingList, saveSearchParams],
  )

  const showFileMappingClients = (fileMappingId: string) => {
    setFileMappingId(fileMappingId)
    setIsFileMappingClientsModalOpen(true)
  }

  const handleCloseFileMappingModal = () => {
    setIsFileMappingClientsModalOpen(false)
  }

  // Restore saved search params when component mounts
    useEffect(() => {
      const savedParams = restoreSearchParams()
      
      if (savedParams) {
        const { isSearchAllColumns, searchTerm } = savedParams
        setTimeout(() => {
          const formSearchColumns = isSearchAllColumns ? 'All' : (savedParams.searchColumns?.[0] || 'All')
          
          outerTableRef.current?.setSearchFormValues?.(
            searchTerm || '',
            formSearchColumns
          )
          if (searchTerm) {
            outerTableRef.current?.setSearchOpen?.(true)
          }
        }, 300)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

  return (
    <Layout.Content>
      {contextHolder}
      <NewCommonTable
        ref={outerTableRef}
        rowData={fileMappingList?.items}
        total={fileMappingList?.total}
        displayUploadButton={false}
        loading={isGettingList}
        translationNamespace='FileMappingSetup:file_mapping_view'
        displayActionColumn
        displayAddBtn={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS) && role !== USER_ROLE.CLIENT }
        displayDownloadBtn={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.DOWNLOAD)}
        pageName={TABLE_NAME.FILE_MAPPING_FORMAT}
        tableSettingName={TABLE_NAME.FILE_MAPPING_FORMAT}
        handleSearch={handleSearch}
        handleDownload={async (payload) => await getListFileMappingFormats(payload)}
        actionDropdownItems={getActionItems}
        handleAddRecord={() => navigate(`${menuUrls.fileMappingSetup}/add`)}
        generateCustomColumnDefs={generateCustomColumnDefs}
        settingHeightTable={settingHeightTableWithFilter}
        defaultParams={defaultParams}
      />

      <ActionConfirmationModal
        icon={<img src={DeleteImg} alt='Confirm Icon' />}
        title={t('FileMappingSetup:actions.delete_format_title')}
        isOpen={isModalConfirmDeleteOpened}
        descriptionType='string'
        description={
          <RichTextField
            richText={
              t('FileMappingSetup:actions.delete_file_mapping_confirm_description', {
                formatName: selectedFileMapping?.formatName,
              }) || ''
            }
          />
        }
        onClose={() => {
          setIsModalConfirmDeleteOpened(false)
        }}
        footer={[
          <Button
            size='middle'
            type='primary'
            key='btn-cancel'
            onClick={() => {
              setIsModalConfirmDeleteOpened(false)
            }}
          >
            {t('common:cancel')}
          </Button>,
          <DeleteButton key='btn-delete' loading={isDeleting} disabled={isDeleting} onClick={handleDeleteFileMapping}>
            {t('common:delete')}
          </DeleteButton>,
        ]}
      />

      <ViewClientsModal
        isOpen={isFileMappingClientsModalOpen}
        fileMappingId={fileMappingId}
        fileMappingName={fileMappingName}
        onClose={handleCloseFileMappingModal}
      />
    </Layout.Content>
  )
}

export default FileMappingTab
